import { useEffect, useRef } from 'react';
import type Hls from 'hls.js';

export const useVideoPlayer = (videoSrc: string, autoPlay = false) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);

  //eslint-disable-next-line sonarjs/cognitive-complexity
  useEffect(() => {
    const videoElement = videoRef.current;
    if (!videoElement || !videoSrc) return;

    let hls: Hls | null = null;

    const tryNative = () => {
      videoElement.src = videoSrc;
      if (autoPlay) {
        const onReady = () => videoElement.play().catch(() => {});
        videoElement.addEventListener('loadedmetadata', onReady, { once: true });
        return () => videoElement.removeEventListener('loadedmetadata', onReady);
      }
    };

    let cleanup: (() => void) | undefined;

    if (videoSrc.endsWith('.m3u8') && videoElement.canPlayType('application/vnd.apple.mpegurl')) {
      cleanup = tryNative();
    } else if (videoSrc.endsWith('.m3u8')) {
      import('hls.js').then(({ default: Hls }) => {
        if (Hls.isSupported()) {
          hls = new Hls();
          hls.loadSource(videoSrc);
          hls.attachMedia(videoElement);
          if (autoPlay) {
            hls.on(Hls.Events.MANIFEST_PARSED, () => {
              videoElement.play().catch(() => {});
            });
          }
        } else {
          cleanup = tryNative();
        }
      });
    } else {
      cleanup = tryNative();
    }

    return () => {
      cleanup?.();
      hls?.destroy();
    };
  }, [videoSrc, autoPlay]);

  return videoRef;
};
